import React, {
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from "react";
import { Modal, Button, Space, message, Spin } from "antd";
import type { FormInstance } from "antd/es/form";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import DynamicForm from "@/components/DynamicForm";
import {
  proxyRegistration,
  getSupplierDetail,
  getSupplierDetailByBusinessId,
  updateSupplierInfo,
  auditSupplierInfo,
  getSupplierListByQcc,
  getSupplierDetailByQcc,
  getSupplierUserSimpleList,
  checkBatchOpenBankNumber,
} from "@/apis/supplier";
import { parseFileUrls } from "@/pages/supplier/SupplierProfile/utils/fileUploadConfig";
import { getCountry, getProvinceList } from "@/apis/addressApi";
import { getRegisterFormItems } from "../config/query";
import { DEPARTMENT_OPTIONS } from "@/constants/contract";
import {
  getModalTitles,
  getButtonLabels,
  type ModalMode,
} from "../config/data";
import styles from "./styles.module.less";

// ==================== 类型定义 ====================
interface RegistrationModalProps {
  open: boolean;
  mode?: ModalMode;
  currentRecord?: any;
  /** 是否为工作台待办模式，如果是则使用 businessId 接口 */
  isFromWorkbench?: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

// ==================== 渲染函数 ====================
const renderModalTitle = (mode: ModalMode, t: (key: string) => string) => {
  const titles = getModalTitles(t);
  return (
    <div className={styles.modalTitleContainer}>
      <span>{titles[mode]}</span>
      <div className={styles.modalTitleDivider} />
    </div>
  );
};

const renderModalFooter = (
  mode: ModalMode,
  onOk: () => void,
  onCancel: () => void,
  submitLoading: boolean,
  t: (key: string) => string,
) => {
  const labels = getButtonLabels(t)[mode];
  const isViewMode = mode === "view";

  return (
    <div className={styles.modalFooterContainer}>
      <Space>
        <Button
          key="cancel"
          className={styles.modalCancelButton}
          onClick={onCancel}
          disabled={submitLoading}
        >
          {labels.cancel}
        </Button>
        {!isViewMode && (
          <Button
            key="submit"
            type="primary"
            className={styles.modalSubmitButton}
            onClick={onOk}
            loading={submitLoading}
            disabled={submitLoading}
          >
            {labels.save}
          </Button>
        )}
      </Space>
    </div>
  );
};

// ==================== 主组件 ====================
const RegistrationModal: React.FC<RegistrationModalProps> = ({
  open,
  mode = "register",
  currentRecord,
  isFromWorkbench = false,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation("supplierProfile");
  const formRef = useRef<FormInstance>(null);
  const detailDataRef = useRef<any>(null);
  const detailLoadedRef = useRef(false); // 防止重复加载详情数据
  const pendingCityValueRef = useRef<any>(null); // 保存待设置的城市值

  // ==================== 表单状态管理 ====================
  const [countryOptions, setCountryOptions] = useState<any[]>([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const [countryMap, setCountryMap] = useState<Record<string, any>>({});
  const [supplierOptions, setSupplierOptions] = useState<any[]>([]);
  const [manufacturerOptions, setManufacturerOptions] = useState<any[]>([]);
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const [supplierSelectLoading, setSupplierSelectLoading] = useState(false);
  const [manufacturerSearchLoading, setManufacturerSearchLoading] =
    useState(false);
  const [provinceBase, setProvinceBase] = useState<any[]>([]);
  const [provinceOptions, setProvinceOptions] = useState<any[]>([]);
  const [provinceLoading, setProvinceLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const supplierSearchTimerRef = useRef<NodeJS.Timeout>();
  const manufacturerSearchTimerRef = useRef<NodeJS.Timeout>();

  // ==================== 辅助函数 ====================
  // 转换 registrant 对象为标准格式
  const transformRegistrant = useCallback(
    (registrant: any) => ({
      surname: registrant.surname || "",
      name: registrant.name || "",
      phone: registrant.phone || "",
      email: registrant.email || "",
      deptCode: registrant.deptCode || "",
      deptName: registrant.deptName || "",
      registrantId: registrant.registrantId || registrant.id || "",
    }),
    [],
  );

  // 从详情数据中提取 registrant 列表
  const extractRegistrantList = useCallback(
    (detailData: any) => {
      if (detailData.registrantList?.length > 0) {
        return detailData.registrantList.map(transformRegistrant);
      }
      if (detailData.manufacturerList?.[0]?.registrant) {
        return [transformRegistrant(detailData.manufacturerList[0].registrant)];
      }
      if (detailData.registrant) {
        return [transformRegistrant(detailData.registrant)];
      }
      return [];
    },
    [transformRegistrant],
  );

  // 反向转换 bankList：将 country 字段转换为 areaCode（用于表单显示）
  const transformBankListForDisplay = useCallback(
    (bankList: any[]) => {
      if (!Array.isArray(bankList)) {
        return bankList;
      }

      return bankList.map((bank: any) => {
        const transformedBank = { ...bank };

        if (bank.country || bank.countryCode) {
          let countryCode = bank.countryCode;

          if (!countryCode && bank.country) {
            const countryValue = bank.country;
            const countryNum = Number(countryValue);

            if (!isNaN(countryNum) && countryNum > 0) {
              if (countryMap[countryValue]) {
                countryCode = countryValue;
              } else {
                const foundCountry = Object.entries(countryMap).find(
                  ([, country]: [string, any]) =>
                    Number(country.areaCode) === countryNum ||
                    String(country.code) === String(countryValue),
                );
                if (foundCountry) {
                  countryCode = foundCountry[0];
                }
              }
            } else {
              const foundCountry = Object.entries(countryMap).find(
                ([, country]: [string, any]) =>
                  country.shortName === countryValue ||
                  country.name === countryValue,
              );
              if (foundCountry) {
                countryCode = foundCountry[0];
              }
            }
          }

          if (countryCode) {
            transformedBank.country = countryCode;
          }
        }

        return transformedBank;
      });
    },
    [countryMap],
  );

  // 转换 bankList 用于提交：将 areaCode 转换为 country 字段值
  const transformBankListForSubmit = useCallback(
    (bankList: any[]) => {
      if (!Array.isArray(bankList)) {
        return bankList;
      }

      return bankList.map((bank: any) => {
        const transformedBank = { ...bank };

        if (bank.country) {
          const countryCode = bank.country;
          const countryData = countryMap[countryCode];
          if (countryData) {
            transformedBank.country = countryData.shortName || countryData.name;
            transformedBank.countryCode = countryData.areaCode;
          }
        }

        return transformedBank;
      });
    },
    [countryMap],
  );

  // 查找国家代码
  const findCountryCode = useCallback(
    (countryValue: string | number) => {
      const foundCountry = Object.entries(countryMap).find(
        ([, country]: [string, any]) =>
          country.name === countryValue || country.areaCode === countryValue,
      );
      return foundCountry?.[0];
    },
    [countryMap],
  );

  // 转换 registrantList 用于提交
  const transformRegistrantListForSubmit = useCallback(
    (registrantList: any[]) => {
      if (!Array.isArray(registrantList) || registrantList.length === 0) {
        return [];
      }

      return registrantList.map((registrant: any) => {
        const deptOption = DEPARTMENT_OPTIONS.find(
          (option: any) => option.value === registrant.deptCode,
        );

        return {
          registrantId: registrant.registrantId || "",
          surname: registrant.surname || "",
          name: registrant.name || "",
          phone: registrant.phone || "",
          email: registrant.email || "",
          deptCode: registrant.deptCode || "",
          deptName: deptOption?.label || "",
        };
      });
    },
    [],
  );

  // ==================== 数据加载函数 ====================
  // 加载国家列表
  const fetchCountries = useCallback(async () => {
    try {
      setCountryLoading(true);
      const response = await getCountry();
      if (response?.data.data && Array.isArray(response.data.data)) {
        const map: Record<string, any> = {};
        const options = response.data.data.map((item: any) => {
          const countryCode = item.areaCode;
          map[countryCode] = item;
          return {
            label: item.name,
            value: countryCode,
          };
        });
        setCountryMap(map);
        setCountryOptions(options);
      }
    } catch (error) {
      message.error(t("获取国家列表失败"));
    } finally {
      setCountryLoading(false);
    }
  }, [t]);

  // 搜索供应商列表
  const handleSupplierSearch = useCallback(async (searchValue: string) => {
    if (supplierSearchTimerRef.current) {
      clearTimeout(supplierSearchTimerRef.current);
    }

    if (!searchValue || searchValue.trim().length === 0) {
      setSupplierOptions([]);
      return;
    }

    supplierSearchTimerRef.current = setTimeout(async () => {
      try {
        setSupplierSearchLoading(true);
        const response = await getSupplierListByQcc(searchValue);
        if (
          response?.data.data.list &&
          Array.isArray(response.data.data.list)
        ) {
          const options = response.data.data.list.map((item: any) => ({
            label: item.supplierName || item.name,
            value: item.id || item.supplierName || item.name,
            data: item,
          }));
          setSupplierOptions(options);
        } else {
          setSupplierOptions([]);
        }
      } catch (error) {
        setSupplierOptions([]);
      } finally {
        setSupplierSearchLoading(false);
      }
    }, 500);
  }, []);

  // 加载省份列表（包含城市数据）- 关键修改：返回最新省份数据
  const loadProvinces = useCallback(
    async (countryCode: string) => {
      try {
        setProvinceLoading(true);
        const response = await getProvinceList(countryCode);
        const baseProvinceData = response.data.data;
        // 1. 更新状态（异步，供其他地方使用）
        setProvinceBase(baseProvinceData);
        console.log("接口返回的省份数据：", baseProvinceData); // 有值
        console.log("当前状态（异步未更新）：", provinceBase); // 旧值（正常现象）

        // 2. 处理省份下拉选项
        if (response?.data?.data && Array.isArray(response.data.data)) {
          const options = response.data.data.map((province: any) => ({
            label: province.name,
            value: province.name,
            isLeaf: false,
            data: {
              code: province.areaCode || province.code,
              name: province.name,
            },
            children: Array.isArray(province.childList)
              ? province.childList.map((city: any) => ({
                  label: city.name,
                  value: city.name,
                  isLeaf: true,
                  data: {
                    code: city.areaCode || city.code,
                    name: city.name,
                  },
                }))
              : [],
          }));
          setProvinceOptions(options);
        } else {
          setProvinceOptions([]);
        }

        // 3. 关键：返回最新的省份数据
        return baseProvinceData;
      } catch (error) {
        message.error(t("加载省份列表失败"));
        setProvinceOptions([]);
        return []; // 异常时返回空数组
      } finally {
        setProvinceLoading(false);
      }
    },
    [t],
  );

  // 匹配城市信息：关键修改 - 接收最新省份数据作为参数，不再依赖provinceBase状态
  const matchCityWithTreeData = useCallback(
    async (
      supplierCity: string,
      supplierCountry: string,
      latestProvinceData?: any[],
    ) => {
      // 检查国家是否包含"中国"
      if (!supplierCountry || !supplierCountry.includes("中国")) {
        return null;
      }

      try {
        // 解析 supplierCity 字符串，格式为 "省份,城市"
        const cityParts = supplierCity.split(",").map((part) => part.trim());
        if (cityParts.length < 2) {
          return null;
        }

        const [provinceName, cityName] = cityParts;
        // 1. 优先使用传入的最新省份数据，没有则调用接口
        console.log("latestProvinceData", latestProvinceData);
        let treeData = latestProvinceData;
        if (!treeData || !Array.isArray(treeData) || treeData.length === 0) {
          const response = await getProvinceList("156");
          if (!response?.data?.data || !Array.isArray(response.data.data)) {
            return null;
          }
          treeData = response.data.data;
        }

        console.log("匹配城市使用的省份数据：", treeData); // 能拿到最新值

        // 2. 在树形数据中查找匹配的省份和城市（使用包含关系匹配）
        for (const province of treeData) {
          if (
            province.name.includes(provinceName) ||
            provinceName.includes(province.name)
          ) {
            if (province.childList && Array.isArray(province.childList)) {
              for (const city of province.childList) {
                if (
                  city.name.includes(cityName) ||
                  cityName.includes(city.name)
                ) {
                  // 找到匹配的城市，返回 cascader 格式的数组（使用 name）
                  return [province.name, city.name];
                }
              }
            }
            break;
          }
        }

        return null;
      } catch (error) {
        console.error("匹配城市失败：", error);
        return null;
      }
    },
    [], // 移除 provinceBase 依赖，改为参数传递
  );

  // 处理供应商选择 - 关键修改：接收loadProvinces返回的最新数据，传给matchCityWithTreeData
  const handleSupplierSelect = useCallback(
    async (value: string, option: any) => {
      try {
        setSupplierSelectLoading(true);
        const supplierData = option?.data;
        if (!supplierData) {
          return;
        }

        const socialCreditCode =
          supplierData.socialCreditCode || supplierData.creditCode;
        if (!socialCreditCode) {
          return;
        }

        const detailResponse = await getSupplierDetailByQcc(socialCreditCode);
        if (detailResponse?.data?.data) {
          const detailData = detailResponse.data.data;
          const socialCreditCodeValue =
            detailData.socialCreditCode || socialCreditCode || t("无");
          const fieldsToUpdate: Record<string, any> = {
            supplierName: detailData.supplierName || detailData.name || value,
            supplierUsName:
              detailData.supplierUsName || detailData.enName || "",
            socialCreditCode: socialCreditCodeValue,
            supplierSocialCreditCode:
              detailData.supplierSocialCreditCode || socialCreditCode,
            supplierLanguage: detailData.supplierLanguage || "",
            supplyInfo: detailData.supplyInfo || detailData.businessScope || "",
            supplierStreet:
              detailData.supplierStreet || detailData.address || "",
          };

          // 处理国家字段 + 加载省份数据
          let latestProvinceData = []; // 存储最新省份数据
          if (detailData.supplierCountry) {
            const countryCode = Number(
              findCountryCode(detailData.supplierCountry),
            );
            // 1. 等待省份加载完成，并接收返回的最新数据
            latestProvinceData = await loadProvinces(
              findCountryCode(detailData.supplierCountry),
            );
            fieldsToUpdate.supplierCountry = countryCode || 156;
          } else {
            fieldsToUpdate.supplierCountry = 156;
            // 若没有国家信息，默认加载中国省份数据
            latestProvinceData = await loadProvinces("156");
          }

          // 处理城市字段 - 关键：把最新省份数据传给matchCityWithTreeData
          if (
            detailData.supplierCity &&
            detailData.supplierCountry?.includes("中国")
          ) {
            const matchedCityCode = await matchCityWithTreeData(
              detailData.supplierCity,
              detailData.supplierCountry,
              latestProvinceData, // 传入最新省份数据
            );
            if (matchedCityCode) {
              fieldsToUpdate.supplierCity = matchedCityCode;
            }
          }

          if (detailData.supplierStartDate && detailData.supplierEndDate) {
            fieldsToUpdate.supplierDateRange = [
              dayjs(detailData.supplierStartDate),
              dayjs(detailData.supplierEndDate),
            ];
          }

          fieldsToUpdate.agentSupplier = detailData.supplierType === 2;

          const bankList = detailData.bankInfoList || detailData.bankList;
          if (Array.isArray(bankList)) {
            fieldsToUpdate.bankList = transformBankListForDisplay(bankList);
          }

          const registrantList = extractRegistrantList(detailData);
          if (registrantList.length > 0) {
            fieldsToUpdate.registrantList = registrantList;
          }

          formRef.current?.setFieldsValue(fieldsToUpdate);
          message.success(t("已自动填充供应商信息"));
        }
      } catch (error) {
        message.error(t("获取供应商详情失败请手动填写"));
      } finally {
        setSupplierSelectLoading(false);
      }
    },
    [
      matchCityWithTreeData,
      transformBankListForDisplay,
      findCountryCode,
      extractRegistrantList,
      loadProvinces,
      t,
    ],
  );

  // 处理生产商选择
  const handleManufacturerSelect = useCallback((value: string, option: any) => {
    // value 是 id，但表单显示 supplierName
    formRef.current?.setFieldsValue({
      manufacturerName: option?.data?.supplierName || option?.label,
    });
  }, []);

  // 搜索生产商列表
  const handleManufacturerSearch = useCallback(async (searchValue: string) => {
    if (manufacturerSearchTimerRef.current) {
      clearTimeout(manufacturerSearchTimerRef.current);
    }

    if (!searchValue || searchValue.trim().length === 0) {
      setManufacturerOptions([]);
      return;
    }

    manufacturerSearchTimerRef.current = setTimeout(async () => {
      try {
        setManufacturerSearchLoading(true);
        const response = await getSupplierUserSimpleList({
          supplierName: searchValue,
        });
        if (response?.data.data && Array.isArray(response.data.data)) {
          const options = response.data.data.map((item: any) => ({
            label: item.supplierName,
            value: item.id,
            data: item,
          }));
          setManufacturerOptions(options);
        } else {
          setManufacturerOptions([]);
        }
      } catch (error) {
        setManufacturerOptions([]);
      } finally {
        setManufacturerSearchLoading(false);
      }
    }, 500);
  }, []);

  // 处理表单值变化
  const handleFormValuesChange = useCallback(
    (changedValues: any, allValues: any) => {
      // 当国家字段变化时，清空省份/城市字段
      if (changedValues.supplierCountry !== undefined) {
        formRef.current?.setFieldsValue({
          supplierCity: undefined,
        });

        // 如果选择了中国，加载省份列表
        const selectedCountryCode =
          changedValues.supplierCountry || allValues.supplierCountry;
        if (selectedCountryCode) {
          const selectedCountry = countryMap[selectedCountryCode];
          if (selectedCountry?.areaCode === 156) {
            // 中国的 areaCode 是 156
            loadProvinces(selectedCountryCode);
          } else {
            setProvinceOptions([]);
          }
        }
      }
    },
    [countryMap, loadProvinces],
  );

  // ==================== 生命周期管理 ====================
  // 弹窗打开时加载基础数据
  useEffect(() => {
    if (open) {
      setModalLoading(true);
      fetchCountries();
      // 设置全局变量以便 custom render 函数获取当前模式
      (window as any).__registrationModalMode = mode;
    }
    return () => {
      // 清理全局变量
      delete (window as any).__registrationModalMode;
    };
  }, [open, mode, fetchCountries]);

  // 编辑模式、查看模式和审核模式下加载供应商详情数据
  useEffect(() => {
    // 确保 countryMap 已加载完成后再加载详情数据,并使用 ref 防止重复加载
    if (
      open &&
      (mode === "edit" || mode === "audit" || mode === "view") &&
      currentRecord?.id &&
      Object.keys(countryMap).length > 0 &&
      !detailLoadedRef.current
    ) {
      detailLoadedRef.current = true; // 标记已加载
      const loadDetailData = async () => {
        try {
          let response;
          if (isFromWorkbench) {
            response = await getSupplierDetailByBusinessId(currentRecord.id);
          } else {
            response = await getSupplierDetail(
              currentRecord.id,
              mode === "audit",
            );
          }
          if (response?.data?.data) {
            const detailData = response.data.data;
            detailDataRef.current = detailData;

            const fieldsToUpdate: Record<string, any> = {
              supplierName: detailData.supplierName || "",
              supplierUsName: detailData.supplierUsName || "",
              socialCreditCode:
                detailData.socialCreditCode ||
                detailData.supplierSocialCreditCode ||
                "",
              supplierSocialCreditCode:
                detailData.supplierSocialCreditCode || "",
              supplierType: detailData.supplierType || "",
              supplierLanguage: detailData.supplierLanguage || "",
              supplyInfo: detailData.supplyInfo || "",
              supplierStreet: detailData.supplierStreet || "",
            };

            let countryCode: string | number | undefined;
            if (detailData.supplierCountryCode) {
              const countryData = countryMap[detailData.supplierCountryCode];
              countryCode =
                countryData?.code ||
                countryData?.areaCode ||
                detailData.supplierCountryCode;
              fieldsToUpdate.supplierCountry = countryCode;
            } else if (detailData.supplierCountry) {
              countryCode = findCountryCode(detailData.supplierCountry);
              if (countryCode) {
                fieldsToUpdate.supplierCountry = countryCode;
              }
            }

            if (detailData.supplierStartDate && detailData.supplierEndDate) {
              fieldsToUpdate.supplierDateRange = [
                dayjs(detailData.supplierStartDate),
                dayjs(detailData.supplierEndDate),
              ];
            }

            // 处理城市字段：中国使用 Cascader 格式，非中国使用字符串
            let cityValue: any = undefined;
            if (
              detailData.supplierCity &&
              (detailData.supplierCountryCode === 156 || countryCode === 156)
            ) {
              // 中国：尝试将逗号分隔的字符串转换为 Cascader 数组格式 [provinceName, cityName]
              const cityParts = detailData.supplierCity
                .split(",")
                .map((part: string) => part.trim());
              if (cityParts.length === 2) {
                // 如果已经是 "省份,城市" 格式，直接使用
                cityValue = cityParts;
              } else {
                // 否则尝试通过 matchCityWithTreeData 匹配
                const matchedCityCode = await matchCityWithTreeData(
                  detailData.supplierCity,
                  detailData.supplierCountry || "中国",
                );
                if (matchedCityCode) {
                  cityValue = matchedCityCode;
                } else {
                  cityValue = detailData.supplierCity;
                }
              }
              // 不立即设置，保存到 ref 中，等省份列表加载完成后再设置
              pendingCityValueRef.current = cityValue;
            } else if (detailData.supplierCity) {
              // 非中国：直接使用字符串
              cityValue = detailData.supplierCity;
              fieldsToUpdate.supplierCity = cityValue;
            }

            // 如果是中国，提前加载省份列表，确保 Cascader 有选项
            // if (detailData.supplierCountryCode === 156 || countryCode === 156) {
            //   await loadProvinces(
            //     String(countryCode || detailData.supplierCountryCode || 156),
            //   );
            // }

            // 保存城市代码信息用于提交
            if (detailData.supplierCityCode || detailData.supplierCity) {
              // 解析城市代码：格式为 "510000,510100"
              const cityCodeParts = detailData.supplierCityCode
                ? detailData.supplierCityCode
                    .split(",")
                    .map((code: string) => code.trim())
                : [];
              detailDataRef.current._cityData = {
                cityCode: detailData.supplierCityCode,
                cityCodeArray: cityCodeParts.length === 2 ? cityCodeParts : [],
                cityName: detailData.supplierCity,
                countryCode: detailData.supplierCountryCode,
              };
            }

            if (
              detailData.manufacturerList &&
              detailData.manufacturerList.length > 0
            ) {
              const manufacturer = detailData.manufacturerList[0];
              const manufacturerName =
                manufacturer.supplierName || manufacturer.name;
              if (manufacturerName) {
                fieldsToUpdate.manufacturerName = manufacturerName;
              }
            }

            const registrantList = extractRegistrantList(detailData);
            if (registrantList.length > 0) {
              fieldsToUpdate.registrantList = registrantList;
              detailDataRef.current._registrantList = registrantList;
            }

            const bankList = detailData.bankInfoList || detailData.bankList;
            if (Array.isArray(bankList)) {
              fieldsToUpdate.bankList = transformBankListForDisplay(bankList);
            }

            if (
              detailData.profileList &&
              Array.isArray(detailData.profileList)
            ) {
              const fileTypeMap: Record<number, string> = {
                0: "businessLicense",
                1: "bankDocs",
                2: "PCAL",
                3: "BAC",
                4: "other",
              };

              detailData.profileList.forEach((profile: any) => {
                const fieldName = fileTypeMap[profile.fileType];
                if (fieldName) {
                  // 使用 parseFileUrls 解析逗号隔开的 fileUrl 字符串
                  const parsedFiles = parseFileUrls(profile.fileUrl);
                  fieldsToUpdate[fieldName] = parsedFiles.map((file) => ({
                    fileId: profile.fileId,
                    name: file.name,
                    url: file.url,
                  }));
                }
              });

              detailDataRef.current._profileList = detailData.profileList;
            }

            // 设置表单值
            formRef.current?.setFieldsValue(fieldsToUpdate);

            // 手动触发 onValuesChange 以确保 DynamicForm 的 stateFormValues 更新
            // 这会触发 custom.render 重新执行，从而更新 BankInfoTable 等自定义组件
            if (window.__dynamicFormOnValuesChange) {
              const allValues = formRef.current?.getFieldsValue();
              window.__dynamicFormOnValuesChange(fieldsToUpdate, allValues);
            }
          }
        } catch (error) {
          message.error(t("加载供应商详情失败"));
        } finally {
          setModalLoading(false);
        }
      };

      loadDetailData();
    } else if (open && mode === "register") {
      detailLoadedRef.current = false; // 注册模式重置标记
      formRef.current?.resetFields();

      const initialValues: Record<string, any> = {
        registrantList: [
          { surname: "", name: "", phone: "", email: "", deptCode: "" },
        ],
        bankList: [
          {
            accountType: undefined,
            country: "",
            openBankName: "",
            openBankNumber: "",
            account: "",
            swiftCode: "",
            accountHolder: "",
          },
        ],
      };

      // 如果从表格操作栏进来，回显供应商名称和邮箱
      if (currentRecord?.supplierName) {
        initialValues.supplierName = currentRecord.supplierName;
      }
      if (currentRecord?.email) {
        initialValues.registrantList[0].email = currentRecord.email;
      }

      formRef.current?.setFieldsValue(initialValues);
      setModalLoading(false);
    }

    // 弹窗关闭或记录切换时重置加载标记
    if (!open || !currentRecord?.id) {
      detailLoadedRef.current = false;
    }
  }, [
    open,
    mode,
    currentRecord?.id,
    currentRecord?.supplierName,
    currentRecord?.email,
    isFromWorkbench,
    countryMap,
    loadProvinces,
    matchCityWithTreeData,
    transformBankListForDisplay,
    extractRegistrantList,
    findCountryCode,
    t,
  ]);

  // 当省份列表加载完成后，设置待处理的城市值
  useEffect(() => {
    if (
      provinceOptions.length > 0 &&
      pendingCityValueRef.current !== null &&
      formRef.current
    ) {
      formRef.current.setFieldsValue({
        supplierCity: pendingCityValueRef.current,
      });
      pendingCityValueRef.current = null;
    }
  }, [provinceOptions]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (supplierSearchTimerRef.current) {
        clearTimeout(supplierSearchTimerRef.current);
      }
      if (manufacturerSearchTimerRef.current) {
        clearTimeout(manufacturerSearchTimerRef.current);
      }
    };
  }, []);

  // ==================== 表单提交处理 ====================
  const handleModalOk = useCallback(() => {
    formRef.current
      ?.validateFields()
      .then(async (values) => {
        const submitData = { ...values };
        if (
          values.supplierDateRange &&
          Array.isArray(values.supplierDateRange)
        ) {
          const [startDate, endDate] = values.supplierDateRange;
          if (startDate && endDate) {
            submitData.supplierStartDate = startDate.format("YYYY-MM-DD");
            submitData.supplierEndDate = endDate.format("YYYY-MM-DD");
          }
        }

        delete submitData.supplierDateRange;

        submitData.agentSupplier = values.supplierType === 2;

        if (values.supplierType) {
          submitData.supplierType = values.supplierType;
        }
        if (values.supplierLanguage) {
          submitData.supplierLanguage = values.supplierLanguage;
        }
        if (values.supplierSocialCreditCode) {
          submitData.supplierSocialCreditCode = values.supplierSocialCreditCode;
        }

        if (values.supplierCountry) {
          const countryCode = values.supplierCountry;
          const countryData = countryMap[countryCode];
          if (countryData) {
            submitData.supplierCountryCode = countryData.areaCode;
            submitData.supplierCountry = countryData.name;
          }
        }

        // 处理省份/城市字段
        if (values.supplierCity) {
          if (Array.isArray(values.supplierCity)) {
            const [provinceName, cityName] = values.supplierCity;
            submitData.supplierCity = `${provinceName},${cityName}`;

            const provinceOption = provinceOptions.find(
              (p: any) => p.value === provinceName,
            );
            if (provinceOption) {
              const cityOption = provinceOption.children?.find(
                (c: any) => c.value === cityName,
              );
              if (cityOption && provinceOption.data && cityOption.data) {
                submitData.supplierCityCode = `${provinceOption.data.code},${cityOption.data.code}`;
              }
            }
          } else {
            submitData.supplierCity = values.supplierCity;
            if (detailDataRef.current?._cityData?.cityCode) {
              submitData.supplierCityCode =
                detailDataRef.current._cityData.cityCode;
            } else {
              submitData.supplierCityCode = values.supplierCity;
            }
          }
        }

        if (values.manufacturerName) {
          const selectedManufacturer = manufacturerOptions.find(
            (option: any) => option.label === values.manufacturerName,
          );

          if (selectedManufacturer?.data) {
            submitData.manufacturerList = [
              {
                supplierId: selectedManufacturer.data.id,
                supplierName:
                  selectedManufacturer.data.supplierName ||
                  selectedManufacturer.data.name,
              },
            ];
          } else {
            submitData.manufacturerList = [
              {
                supplierName: values.manufacturerName,
              },
            ];
          }
        }

        delete submitData.manufacturerName;

        let profileList: Array<{
          fileId: number;
          fileType: number;
          fileName?: string;
          fileUrl: string;
        }> = [];

        const fileTypeMap: Record<string, number> = {
          businessLicense: 0,
          bankDocs: 1,
          PCAL: 2,
          BAC: 3,
          other: 4,
        };

        // 从表单值中收集文件数据（支持编辑/审核模式下的文件修改）
        Object.entries(fileTypeMap).forEach(([fieldName, fileType]) => {
          const fileList = values[fieldName];
          if (fileList && Array.isArray(fileList)) {
            fileList.forEach((file: any) => {
              if (file.url && file.fileId) {
                profileList.push({
                  fileId: file.fileId,
                  fileType,
                  fileName: file.name || file.fileName,
                  fileUrl: file.url,
                });
              }
            });
          }
        });

        if (profileList.length > 0) {
          submitData.profileList = profileList;
        }

        const fileFieldsToDelete = [
          "businessLicense",
          "bankDocs",
          "BAC",
          "PCAL",
          "other",
        ];
        fileFieldsToDelete.forEach((field) => delete submitData[field]);

        const bankListData = formRef.current?.getFieldValue("bankList");
        if (
          bankListData &&
          Array.isArray(bankListData) &&
          bankListData.length > 0
        ) {
          submitData.bankList = transformBankListForSubmit(bankListData);
        } else {
          if (
            !submitData.bankList ||
            (Array.isArray(submitData.bankList) &&
              submitData.bankList.length === 0)
          ) {
            delete submitData.bankList;
          }
        }

        // 处理 registrantList 字段
        if (mode === "audit" && detailDataRef.current?._registrantList) {
          submitData.registrantList = detailDataRef.current._registrantList;
        } else if (values.registrantList?.length > 0) {
          submitData.registrantList = transformRegistrantListForSubmit(
            values.registrantList,
          );
        }

        const registrantFieldsToDelete = [
          "surname",
          "name",
          "phone",
          "deptCode",
          "email",
        ];
        registrantFieldsToDelete.forEach((field) => delete submitData[field]);

        try {
          setSubmitLoading(true);

          // 检测银行开户行行号是否在SAP中存在（仅提示，不阻止提交）
          if (
            submitData.bankList &&
            Array.isArray(submitData.bankList) &&
            submitData.bankList.length > 0
          ) {
            const openBankNumbers = submitData.bankList
              .map((bank: any) => bank.openBankNumber)
              .filter((num: string) => num);

            if (openBankNumbers.length > 0) {
              try {
                const checkResponse = await checkBatchOpenBankNumber({
                  openBankNumbers,
                });
                if (
                  checkResponse?.data?.code === 200 &&
                  checkResponse?.data?.data
                ) {
                  const notFoundBankNumbers = checkResponse.data.data;
                  if (
                    Array.isArray(notFoundBankNumbers) &&
                    notFoundBankNumbers.length > 0
                  ) {
                    // 等待用户点击"知道了"继续提交，或点击"取消"返回修改
                    const shouldContinue = await new Promise<boolean>(
                      (resolve) => {
                        Modal.confirm({
                          title: t("提示"),
                          content: `${t("检测到SAP不存在当前开户行信息")}`,
                          okText: t("知道了"),
                          cancelText: t("取消"),
                          onOk: () => resolve(true),
                          onCancel: () => resolve(false),
                        });
                      },
                    );
                    if (!shouldContinue) {
                      setSubmitLoading(false);
                      return;
                    }
                  }
                }
              } catch (checkError) {
                // 检测失败不影响提交
              }
            }
          }

          if (mode === "register") {
            await proxyRegistration(submitData);
            message.success(t("代注册成功"));
          } else if (mode === "edit") {
            const editData = {
              ...submitData,
              supplierId: detailDataRef.current?.id || currentRecord?.id,
            };
            await updateSupplierInfo(editData);
            message.success(t("编辑成功"));
          } else if (mode === "audit") {
            const auditData = {
              ...submitData,
              supplierId: detailDataRef.current?.id || currentRecord?.id,
              auditStatus: submitData.auditStatus,
              auditOpinion: submitData.auditOpinion,
            };
            await auditSupplierInfo(auditData);
            message.success(t("审核成功"));
          }

          onCancel();
          formRef.current?.resetFields();
          onSuccess?.();
        } catch (error) {
        } finally {
          setSubmitLoading(false);
        }
      })
      .catch((errorInfo) => {
        message.error(t("请完善必填项"));
      });
  }, [
    countryMap,
    manufacturerOptions,
    provinceOptions,
    onCancel,
    onSuccess,
    mode,
    currentRecord,
    transformBankListForSubmit,
    transformRegistrantListForSubmit,
    t,
  ]);

  // ==================== 动态配置项 ====================
  const updateFormItemWidget = useCallback(
    (item: any) => {
      const widgetUpdates: Record<string, any> = {
        supplierName: {
          options: supplierOptions,
          loading: supplierSearchLoading || supplierSelectLoading,
          onSearch: handleSupplierSearch,
          onSelect: handleSupplierSelect,
        },
        manufacturerName: {
          options: manufacturerOptions,
          loading: manufacturerSearchLoading,
          onSearch: handleManufacturerSearch,
          onSelect: handleManufacturerSelect,
        },
        supplierType: {
          options: [
            { label: t("生产商"), value: 1 },
            { label: t("代理商"), value: 2 },
          ],
        },
        supplierCountry: {
          options: countryOptions,
          loading: countryLoading,
        },
        supplierCity: {
          options: provinceOptions,
          loading: provinceLoading,
        },
      };

      if (widgetUpdates[item.name]) {
        return {
          ...item,
          widget: {
            ...item.widget,
            ...widgetUpdates[item.name],
          },
        };
      }
      return item;
    },
    [
      supplierOptions,
      supplierSearchLoading,
      supplierSelectLoading,
      handleSupplierSearch,
      handleSupplierSelect,
      manufacturerOptions,
      manufacturerSearchLoading,
      handleManufacturerSearch,
      handleManufacturerSelect,
      countryOptions,
      countryLoading,
      provinceOptions,
      provinceLoading,
      t,
    ],
  );

  const registerFormItems = useMemo(() => {
    const items = getRegisterFormItems(mode, isFromWorkbench, countryMap, t);
    return items.map(updateFormItemWidget);
  }, [mode, isFromWorkbench, countryMap, t, updateFormItemWidget]);

  const formConfig = useMemo(
    () => ({
      labelCol: { span: 6 },
      wrapperCol: { span: 18 },
      onValuesChange: handleFormValuesChange,
      disabled: mode === "view",
    }),
    [handleFormValuesChange, mode],
  );

  return (
    <Modal
      title={renderModalTitle(mode, t)}
      open={open}
      onOk={handleModalOk}
      onCancel={onCancel}
      width={1300}
      centered={false}
      className={styles.registrationModal}
      styles={{
        header: {
          paddingBottom: "16px",
          marginBottom: "0",
          borderBottom: "1px solid #f0f0f0",
          marginLeft: "-24px",
          marginRight: "-24px",
          paddingLeft: "24px",
          paddingRight: "24px",
        },
        body: {
          maxHeight: "calc(100vh - 200px)",
          overflowY: "auto",
          paddingRight: "36px",
          paddingTop: "16px",
          marginTop: "8px",
        },
      }}
      footer={(_, { OkBtn, CancelBtn }) =>
        renderModalFooter(mode, handleModalOk, onCancel, submitLoading, t)
      }
    >
      <Spin spinning={modalLoading} tip={t("加载中...")}>
        <DynamicForm
          ref={formRef}
          isViewMode={mode === "view"}
          formItem={registerFormItems}
          formConfig={formConfig}
        />
      </Spin>
    </Modal>
  );
};

export default RegistrationModal;
